import {ServicesConfigurationsWithIdSchema, ServiceSurveyResponsesWithIdSchema} from "../../../types";
import {
  CONNETTORI_INPUT_TYPES,
  CONNETTORI_LEGNO_INPUT_STRUCTURE,
  CONNETTORI_VERTICALI_INPUT_STRUCTURE
} from "../../../services/connettori";
import { 
  TIPOLOGIA_OPTIONS, 
  CLASSE_CLS_OPTIONS_VALUES, 
  TENSILE_CURB_OPTIONS, 
  SYSTEM_OPTIONS, 
  ENVIRONMENTAL_EXPOSURE_OPTIONS, 
  WALL_FLOOR_CONNECTION_OPTIONS 
} from "feature/services/connettori/custom-input-rinforzo/lib/consts";
import {ConnectorResponses} from "../../../services/connettori/types";
import {CustomInputRinforzoValue} from "../../../services/connettori/custom-input-rinforzo/types";

const extractResponseValue = (questionId: number, responses: Record<number, any>, required: boolean) => {
  const response = responses[questionId];
  if (!response && required)
    throw Error(`Unexpected null response (question ID ${JSON.stringify(questionId)})`);
  if (!!response?.option)
    return response.option.option_value;
  return Array.isArray(response?.value) ? response.value[0] : response?.value;
}

export const extractConnectorResponses = (
  {calculation}: ServicesConfigurationsWithIdSchema,
  responses: {[key: number]: any},
  csvs?: Record<string, any[]>,
) => {
  if (calculation?.length !== 1)
    throw Error('malformed output');

  const obj = JSON.parse(calculation[0]);
  if (obj.original !== "connector")
    throw Error('not a connector calculation');

  const {connector_type: type} = obj;
  if (!type || type === "diaphragm") {
    return CONNETTORI_INPUT_TYPES.reduce(
      (prev, curr) => ({
        ...prev,
        [curr]: responses[obj[`${curr}_question`]]?.value,
      }),
      {} as { [key: string]: any }
    )
  } else if (type === "vertical" || type === "wood") {

    const {blocks_mappings} = obj;
    if (!blocks_mappings)
      throw Error(`malformed ${type} connector output`);

    // Collect technical values
    const structure = type === "vertical"
      ? CONNETTORI_VERTICALI_INPUT_STRUCTURE
      : CONNETTORI_LEGNO_INPUT_STRUCTURE;
    const technicalValues = Object.entries(structure).reduce(
      (acc, [group, data]) => {
        const groupResponses: Record<string, number> = {};
        const isTopLevel = group === "__";
        const groupMappings = isTopLevel ? blocks_mappings : blocks_mappings[group];
        if (!groupMappings)
          throw Error(`missing mappings for group: ${group}`);
        Object.keys(data).forEach((key) => {
          if ("fixedNumber" in data[key]) {
            if (groupMappings[key] === undefined){
              throw Error(`missing fixed number for group: ${group}, key: ${key}`);
            }
            groupResponses[key] = Number(groupMappings[key]);
          } else {
            if (!("questions" in data[key])) {
              // CASE A: direct question
              // --- Try to extract the value from the response
              const value = extractResponseValue(groupMappings[key], responses, false) ?? data[key].default;
              if (value === undefined)
                throw Error(`missing default value for group: ${group}, key: ${key}`);
              // --- Store the value
              groupResponses[key] = value;
            } else {
              // CASE B: computed value
              const {
                questions,
                compute,
                default: defaultValue,
              } = data[key];
              if (!compute)
                throw Error(`missing compute function for group: ${group}, key: ${key}`);
              const questionsResponses = Object.keys(questions || {})
                .reduce(
                  (acc, curr) => ({
                    ...acc,
                    [curr]: extractResponseValue(groupMappings[key][curr], responses, false),
                  }),
                  {} as {[key: string]: any}
                );
              groupResponses[key] = compute(questionsResponses, csvs) ?? defaultValue;
            }
          }
        })
        const toAdd = isTopLevel ? groupResponses : {[group]: groupResponses};
        return {...acc, ...toAdd};
      },
      {} as {[key: string]: any}
    );

    // Collect survey input values
    let surveyValues = {};
    if (type === "vertical") {
      surveyValues = {
        final_system_choice: responses[blocks_mappings._system_choice_question]?.option?.option_value,
        concrete_cls: extractResponseValue(
          blocks_mappings["Existing Materials"]["fcm"]["cls"],
          responses,
          false,
        ),
        steel_cls: extractResponseValue(
          blocks_mappings["Existing Materials"]["fym"]["cls"],
          responses,
          false,
        ),
      };
    } else {  // type === "wood"
      surveyValues = {
        selected: responses[blocks_mappings._connector_choice_question]?.option?.option_value,
      }
    }
    
    return {
      ...technicalValues,
      FromConfiguratorInput: surveyValues,
    }
  }
  else
    throw Error(`Unsupported connector type: ${type}`);
}


function calculateSlabProperties(rinforzo: CustomInputRinforzoValue) {
  let reinf_type = 0
  for (const key in TIPOLOGIA_OPTIONS) {
    if (key === rinforzo.tipologia_rinforzo) {
      break;
    }
    reinf_type++;
  }

  let fck = 0
  let d = "0"
  let passo = "0"
  const hs = rinforzo.spessore_soletta

  if (reinf_type >= 2) {
    d = rinforzo.diametro_armatura_b450c || d;
    passo = (rinforzo.passo_rete || passo).toString();
  } 
  if (reinf_type === 3) {
    fck = CLASSE_CLS_OPTIONS_VALUES[rinforzo.classe_cls!]
  }

  return { fck, d, passo, hs}
}

function calculateReinfType(tipologia_rinforzo: string) {
  let reinf_type = 0
  for (const key in TIPOLOGIA_OPTIONS) {
    if (key === tipologia_rinforzo) {
      break;
    }
    reinf_type++;
  }
  return reinf_type
}

function calculateConnectorType(wall_floor_connector: string) {
  let connetorType = 0
  for (const key in WALL_FLOOR_CONNECTION_OPTIONS) {
    if (key === wall_floor_connector) {
      break;
    }
    connetorType++;
  }
  return connetorType
}

function calculateTensionCurbProperties(rinforzo: CustomInputRinforzoValue) {
  let type = 0
  let FRCMtype = 0
  let ExposureType = 0
  let nf = 0
  let bf = 0
  let nb = 0
  let db = 0
  for (const key in TENSILE_CURB_OPTIONS) {
    if (key === rinforzo.tipologia_cordolo) {
      break;
    }
    type++;
  }
  if (type === 0 && rinforzo.numero_strati_cordolo && rinforzo.altezza_fascia_cordolo) {
    for (const key in SYSTEM_OPTIONS) {
      if (key === rinforzo.sistema_cordolo) {
        break;
      }
      FRCMtype++;
    }
    for (const option of ENVIRONMENTAL_EXPOSURE_OPTIONS) {
      if (option === rinforzo.esposizione_ambientale) {
        break;
      }
      ExposureType++;
    }
    nf = rinforzo.numero_strati_cordolo
    bf = rinforzo.altezza_fascia_cordolo
  } else if (type === 1 && rinforzo.barre_b450c_cordolo && rinforzo.diametro_barre_b450c_cordolo) {
    nb = rinforzo.barre_b450c_cordolo 
    db = rinforzo.diametro_barre_b450c_cordolo 
  }
  return {type, FRCMtype, ExposureType, nf, bf, nb, db }
}

export const buildPayloadAsdea = (responses: ConnectorResponses) => {

  const { 
    normativa,
    resistance, 
    confidence, 
    security, 
    specificWeight, 
    Le, 
    B, 
    Bm3, 
    Bm4, 
    S1, 
    S2, 
    S3, 
    S4,
    Hup, 
    Hbottom,
    Hcordolo
  } = responses.geometria;
  const { 
    tipologia_rinforzo, 
    resistenza_taglio, 
    wall_floor_connector, 
    barre_b450c_rinforzo,
    diametro_barre_b450c_rinforzo
  } = responses.rinforzo;
  const { 
    caricoStrutturale, 
    caricoPortato, 
    caricoVariabile, 
    coefficienteCombinazione, 
    ag, 
    comportamento, 
    amplificazione, 
    solaio, 
    altezza, 
    periodoNonStrutturale, 
    periodoStruttura 
  } = responses.carichi;

  const normativaCode = normativa === "ec8" ? 0 : 1

  const reinf_type = calculateReinfType(tipologia_rinforzo)

  const { fck, d, passo, hs} = calculateSlabProperties(responses.rinforzo)

  const {type, FRCMtype, ExposureType, nf, bf, nb, db } = calculateTensionCurbProperties(responses.rinforzo)

  const connetorType = calculateConnectorType(wall_floor_connector)
  return {
    GeneralData: {
      version: "1.0.0",
    },
    MasonryMaterialProperties: {
      fhm: Math.abs(resistance), 
      FC: confidence,
      gamma: security,
      w: specificWeight,
    },
    MasonryWallProperties: {
      Le: Le,
      B: B,
      Bm3: Bm3,
      Bm4: Bm4,
      s1: S1,
      s2: S2,
      s3: S3,
      s4: S4,
      hup: Hup,
      hbot: Hbottom,
    },
    SlabProperties: {
      reinf_type: reinf_type,
      fck: fck, 
      hs: hs,
      d: d,
      passo: passo,
      d_add: diametro_barre_b450c_rinforzo || 0, 
      n_add: barre_b450c_rinforzo || 0 
    },
    CompressionCurbProperties: { 
      type: 0,
      // @ts-ignore
      bc: Math.min(parseFloat(S1), parseFloat(S2), parseFloat(S3), parseFloat(S4)),
      hc: Hcordolo,
      fysk: 0,
      gammas: 0,
      An: 0,
      ry: 0,
      rz: 0,
      ly: 0,
      lz: 0,
      cy: 0,
      cz: 0
    },
    TensionCurbProperties: {
      type: type,
      FRCMtype: FRCMtype,
      ExposureType: ExposureType,
      nf: nf,
      bf: bf,
      nb: nb,
      db: db,
      fysk: 0,
      gammas: 0,
      Ap: 0,
    },
    ConnectorsProperties: {
      type: connetorType,
      Rm: resistenza_taglio,
      FC: 1,
      Gamma: 2,
    },
    Loads: {
      // @ts-ignore
      Gk1_Gk2: parseFloat(caricoStrutturale) + parseFloat(caricoPortato),
      Qk: caricoVariabile,
      psi: coefficienteCombinazione,
      ag: ag,
      q: comportamento,
      S: amplificazione,
      zi: solaio,
      Htot: altezza,
      Ta: periodoNonStrutturale,
      T1: periodoStruttura,
      code: normativaCode,
    },
  };
};
